
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUserManagement } from '@/hooks/useUserManagement';
import { toast } from 'sonner';

interface DeleteUserButtonProps {
  userId: string;
  userName: string;
  onDeleted?: () => void;
}

export function DeleteUserButton({ userId, userName, onDeleted }: DeleteUserButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { deleteUser, isDeleting } = useUserManagement();

  const handleDelete = async () => {
    try {
      const toastId = toast.loading(`Deleting user ${userName}...`, {
        description: "This may take a moment"
      });
      
      const success = await deleteUser(userId);
      
      toast.dismiss(toastId);
      
      if (success) {
        toast.success(`User ${userName} deleted`, {
          description: "All associated data has been permanently removed"
        });
        
        if (onDeleted) {
          onDeleted();
        }
      }
      
      setIsDialogOpen(false);
    } catch (error: any) {
      console.error("Error in DeleteUserButton:", error);
      toast.error("Delete operation failed", {
        description: error.message || "An unexpected error occurred"
      });
      setIsDialogOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
        onClick={() => setIsDialogOpen(true)}
      >
        <Trash2 className="h-4 w-4" />
        <span>Delete</span>
      </Button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete user <strong>{userName}</strong> and all their related data including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Profile information</li>
                <li>Created prompts</li>
                <li>Collections and saved prompts</li>
                <li>Reviews and likes</li>
                <li>Authentication record</li>
                <li>Chat sessions and messages</li>
                <li>Subscriptions</li>
              </ul>
              <p className="mt-2 text-amber-600 dark:text-amber-400 font-semibold">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
